import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ArrayHelper } from 'src/app/helpers/array-helper';
import { ITchatRoom } from 'src/app/models/server/interface/tchat-room.interface';
import { TchatRoom } from 'src/app/models/server/tchat-room';
import { TchatUser } from 'src/app/models/server/tchat-user';
import { TchatUserNotification } from 'src/app/models/tchat-notification';
import { ConnectedUsersStateService } from './connected-users-state.service';

@Injectable({
    providedIn: 'root',
})
export class TChatStateService {
    public menuIsOpen: boolean = true;

    private users = new BehaviorSubject<TchatUser[]>([]);

    private rooms = new BehaviorSubject<TchatRoom[]>([]);

    private roomSelected = new BehaviorSubject<TchatRoom>(null);

    private notifications = new BehaviorSubject<TchatUserNotification[]>([]);

    constructor(public connectedUserStateService: ConnectedUsersStateService) {}

    /**
     * Emet un evement lors qu'il y a une notificaiton de tchat a afficher
     */
    public get notifications$() {
        return this.notifications.asObservable();
    }

    /**
     * Retourne un utilisateur par rapport a son login
     * @param login login de l'utilisateur
     * @returns retourne un utilistateur
     */
    public getUser(login: string): TchatUser {
        return this.users.value.find((x) => x.login == login);
    }

    public getUsers(): TchatUser[] {
        return this.users.value;
    }

    /**
     * Emet un événement lors que l'etat d'un utilisateur change.
     */
    public get getUsers$() {
        return this.users.asObservable();
    }

    /**
     * Met à jour les listes des utilisateurs.
     * @param users liste utilisateur.
     */
    public setUsers$(users: TchatUser[]) {
        let values = this.users.value;

        if (values.length != users.length) {
            if (values.length != 0) {
                values.forEach((value) => {
                    users.find((x) => x.login == value.login).online = value.online;
                });
                this.users.next(null);
            }

            users.forEach((u) => {
                let n = this.notifications.value.find((n) =>
                    ArrayHelper.arrayIsEqual(
                        n.users.map((x) => x.login),
                        [u.login, this.connectedUserStateService.getUserConnected().login]
                    )
                );
                let value = !!n ? n.notification : 0;
                u.notifications = value;
            });

            this.users.next(users);
        }
    }

    /**
     * Mise à jour la variable online d'un utilisateur.
     * @param login nom de l'utilisateur
     * @param online valeur de online
     */
    public setUserOnline(login: string, online: boolean) {
        let values = this.users.value;

        if (values.length == 0) {
            let user: TchatUser = new TchatUser(login, login, online, null);
            values.push(user);
        } else {
            let user = values.find((x) => x.login == login);

            if (user) {
                user.online = online;
            } else {
                console.warn(`Impossible de setter l'utilisateur '${login}' en online car il n'est pas dans la liste`, values);
            }
        }

        this.users.next(values);
    }

    /**
     * Ajout d'une conversation dans la liste des conversations.
     * @param room Information de la conversation
     */
    public addRoom(iroom: ITchatRoom): void {
        let room = TchatRoom.createFormCopy(iroom);
        room.target = this.getRoomTarget(room);

        let rooms = this.rooms.value;

        rooms.push(room);
        this.rooms.next(rooms);

        if (
            !ArrayHelper.arrayIsEqual(
                room.users.map((x) => x.login),
                this.roomSelected.value?.users.map((x) => x.login)
            )
        ) {
            this.addNotification(room.users);
        } else {
            this.roomSelected.next(room);
        }
    }

    /**
     * Retire une conversation dans la liste des conversation.
     * @param room Information de la conversation.
     */
    public removeRoom(iroom: ITchatRoom): void {
        let room = TchatRoom.createFormCopy(iroom);
        room.target = this.getRoomTarget(room);

        let rooms = this.rooms.value.filter((x) => x.guid !== room.guid);

        this.rooms.next(rooms);

        let selectedRoom = rooms.length !== 0 ? rooms[0] : null;
        this.roomSelected.next(selectedRoom);
    }

    /**
     * Mise à jour de la conversation
     * @param room Information de la conversation
     */
    public setRoom(iroom: ITchatRoom): void {
        let room = TchatRoom.createFormCopy(iroom);
        room.target = this.getRoomTarget(room);

        let values = this.rooms.value;
        values.find((r) => r.guid == room.guid).messages = room.messages;
        this.rooms.next(values);

        if (room.guid != this.roomSelected.value?.guid) {
            this.addNotification(room.users);
        } else {
            this.roomSelected.next(room);
        }
    }

    /**
     * Récupère une conversation existant sinon la crée
     * @param sourceUserLogin Utilsiateur source
     * @param targetsUserlogin Utilisateur destinataire
     * @returns retourne une conversation
     */
    public room(users: TchatUser[]): TchatRoom {
        let room = this.rooms.value.find((r) =>
            ArrayHelper.arrayIsEqual(
                r.users.map((x) => x.login),
                users.map((x) => x.login)
            )
        );

        if (!!!room) {
            room = new TchatRoom(users);
            room.target = this.getRoomTarget(room);
        }
        return room;
    }

    /**
     * Emet un événement lors que l'etat d'une conversation change.
     */
    public get rooms$(): Observable<TchatRoom[]> {
        return this.rooms.asObservable();
    }

    /**
     * Recupère la valeur conversation selectionné
     */
    public get roomSelected$() {
        return this.roomSelected.value;
    }

    /**
     * Emet un événement lors du changment de selection
     */
    public get roomSelectedChange$() {
        return this.roomSelected.asObservable();
    }

    /**
     * Met a jour la selection de la conversation.
     * @param room
     */
    public roomSelectedChange(room: TchatRoom): void {
        if (!!room) {
            this.resetNotification(room.users);
        }

        this.roomSelected.next(room);
    }

    /**
     * Ajout d'une notification
     */
    private addNotification(users: TchatUser[]): void {
        let notification = this.notifications.value.find((n) =>
            ArrayHelper.arrayIsEqual(
                n.users.map((x) => x.login),
                users.map((x) => x.login)
            )
        );

        if (!!notification) {
            notification.notification = notification.notification + 1;
        } else {
            this.notifications.value.push({ users, notification: 1 });
        }

        this.notifications.next(this.notifications.value);
    }

    /**
     * Supression d'une notifications
     */
    private resetNotification(users: TchatUser[]) {
        let notification = this.notifications.value.find((n) =>
            ArrayHelper.arrayIsEqual(
                n.users.map((x) => x.login),
                users.map((x) => x.login)
            )
        );

        if (!!notification) {
            this.notifications.value.splice(this.notifications.value.indexOf(notification), 1);
            this.notifications.next(this.notifications.value);
        }
    }

    /**
     * Récupère l'utilisateur cible.
     */
    public getRoomTarget(room: ITchatRoom): TchatUser {
        let targets = room.users.filter((x) => x.login != this.connectedUserStateService.getUserConnected().login);

        if (targets.length == 1) {
            return targets[0];
        } else {
            let displayName: string = '';
            let login: string = '';
            targets.forEach((target) => {
                displayName = `${displayName}${target.displayName}; `;
                login = `${login}${target.login}; `;
            });

            let user: TchatUser = new TchatUser(login, displayName, null, null, 'GR');
            return user;
        }
    }
}
